import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { extractErrorMessage } from '@/shared/api/errors';

type UseMutateOptions<TData, TVariables> = {
  api: (variables: TVariables) => Promise<TData>;
  invalidateKeys?: QueryKey[];
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: Error, message: string, variables: TVariables) => void;
} & Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn' | 'onSuccess' | 'onError'>;

export const useMutate = <TData, TVariables = void>({
  api,
  invalidateKeys,
  onSuccess,
  onError,
  ...rest
}: UseMutateOptions<TData, TVariables>) => {
  const qc = useQueryClient();
  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables) => api(variables),
    onSuccess: (data, variables) => {
      invalidateKeys?.forEach((key) => qc.invalidateQueries({ queryKey: key }));
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, extractErrorMessage(error), variables);
    },
    ...rest,
  });
};
